// TanSS → SAP Draft Sync — React Query API-Hooks

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'

// ─── Typen ────────────────────────────────────────────────────────────────────

export type QueueStatus = 'pending' | 'processing' | 'error' | 'completed'
export type TanssSapAiMode = 'always_ai' | 'never_ai' | 'customer_default_no' | 'customer_default_yes'

export interface QueueEntry {
  id: string
  tenantId: string
  tanssBillId: string
  tanssCustomerId: string | null
  sapCardCode: string | null
  status: QueueStatus
  errors: Array<{ type?: string; message: string; lineIndex?: number }> | null
  retryCount: number
  lastTriedAt: string | null
  createdAt: string
}

export interface QueueItem {
  id: string
  queueId: string
  lineIndex: number
  itemCode: string | null
  description: string | null
  itemDetails: string | null
  quantity: string
  unitPrice: string
  positionType: 'service' | 'fahrt' | 'material'
  tanssTicketId: string | null
  errors: Array<{ message: string }> | null
  isActive: boolean
  overrides: Record<string, unknown> | null
  createdAt: string
}

export interface QueueEntryDetail extends QueueEntry {
  items: QueueItem[]
  mappedPayload: Record<string, unknown> | null
  rawTanssData: unknown
  sapDocEntry: number | null
}

export interface TanssSapSettings {
  aiMode: TanssSapAiMode
  debugMode: boolean
  cronPattern: string
  serviceItemCode: string
  fahrtkostenItemCode: string
  useTanssArticleNumber: boolean
  disableItemErrors: boolean
  daysBack: number
  serviceTemplateId: string | null
}

// ─── Queue-Liste ──────────────────────────────────────────────────────────────

export function useTanssSapQueue(params: { page?: number; pageSize?: number; status?: QueueStatus | '' } = {}) {
  const { page = 1, pageSize = 20, status } = params
  return useQuery<{ data: QueueEntry[]; total: number; page: number; pageSize: number }>({
    queryKey: ['tanss-sap', 'queue', page, pageSize, status],
    queryFn: () => {
      const query = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
      if (status) query.set('status', status)
      return apiClient.get(`/tanss-sap/queue?${query}`).then((r) => r.data)
    },
  })
}

// ─── Queue-Detail ─────────────────────────────────────────────────────────────

export function useTanssSapQueueEntry(id: string | null) {
  return useQuery<{ data: QueueEntryDetail }>({
    queryKey: ['tanss-sap', 'queue', id],
    queryFn: () => apiClient.get(`/tanss-sap/queue/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

// ─── Position korrigieren ─────────────────────────────────────────────────────

export function usePatchQueueItem() {
  const qc = useQueryClient()
  return useMutation<{ data: QueueItem }, Error, { queueId: string; itemId: string; overrides: Record<string, unknown> }>({
    mutationFn: ({ queueId, itemId, overrides }) =>
      apiClient.patch(`/tanss-sap/queue/${queueId}/items/${itemId}`, { overrides }).then((r) => r.data),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['tanss-sap', 'queue', vars.queueId] })
    },
  })
}

// ─── Retry ───────────────────────────────────────────────────────────────────

export function useRetryQueueEntry() {
  const qc = useQueryClient()
  return useMutation<{ data: { success: boolean; sapDocEntry: number } }, Error, string>({
    mutationFn: (id) => apiClient.post(`/tanss-sap/queue/${id}/retry`).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tanss-sap', 'queue'] })
    },
  })
}

// ─── Manueller Sync ──────────────────────────────────────────────────────────

export interface SyncCustomerEntry {
  companyId: number
  sapCardCode: string | null
  status: 'created' | 'error' | 'skipped'
  tenantId: string
  errorMessage?: string
}

export interface TanssSapSyncRun {
  id: string
  tenantId: string | null
  processed: number
  createdCount: number
  skipped: number
  errors: number
  customers: SyncCustomerEntry[] | null
  sapCompanyDb: string | null
  isDryRun: boolean
  dryRunDetails: TanssSapDryRunDetails | null
  createdAt: string
}

export interface DryRunDocumentLine {
  supportId: number
  itemCode: string
  itemDescription: string
  itemDetailsWithAi: string
  itemDetailsWithoutAi: string
  quantity: number
  unitPrice: number
  ticketId?: number
}

export interface DryRunCustomerEntry {
  companyId: number
  companyName: string | null
  tenantId: string
  supportCount: number
  supportIds: number[]
  documentLines: DryRunDocumentLine[]
  sapPayloadWithAi: Record<string, unknown>
  sapPayloadWithoutAi: Record<string, unknown>
}

export interface TanssSapDryRunDetails {
  isDryRun: true
  note: string
  targetDocType: 'draft'
  settings: {
    aiMode: string
    serviceItemCode: string
    serviceTemplateId: string | null
    daysBack: number
  }
  customers: DryRunCustomerEntry[]
}

export interface TanssSapDryRunResult {
  runId: string
  processedCompanies: number
  sapCompanyDb: string | null
  details: TanssSapDryRunDetails
}

export function useTanssSapSyncRuns() {
  return useQuery<TanssSapSyncRun[]>({
    queryKey: ['tanss-sap', 'sync-runs'],
    queryFn: () => apiClient.get('/tanss-sap/sync-runs').then((r) => r.data.data),
    staleTime: 30_000,
  })
}

export function useTriggerSync() {
  const qc = useQueryClient()
  return useMutation<{ data: { processed: number; created: number; skipped: number; errors: number; customers: SyncCustomerEntry[] } }, Error, { tenantId?: string }>({
    mutationFn: (payload) => apiClient.post('/tanss-sap/sync', payload).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tanss-sap'] })
      void qc.invalidateQueries({ queryKey: ['billing-auto', 'runs'] })
      void qc.invalidateQueries({ queryKey: ['tanss-sap', 'sync-runs'] })
    },
  })
}

// ─── Einstellungen ────────────────────────────────────────────────────────────

export function useTanssSapSettings() {
  return useQuery<{ data: TanssSapSettings }>({
    queryKey: ['tanss-sap', 'settings'],
    queryFn: () => apiClient.get('/tanss-sap/settings').then((r) => r.data),
  })
}

export function useUpdateTanssSapSettings() {
  const qc = useQueryClient()
  return useMutation<{ data: TanssSapSettings }, Error, Partial<TanssSapSettings>>({
    mutationFn: (payload) => apiClient.put('/tanss-sap/settings', payload).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tanss-sap', 'settings'] })
    },
  })
}

// ─── Nicht abgerechnete Leistungen (Baum) ────────────────────────────────────

export interface TanssUninvoicedSupport {
  id: number
  employeeId: number
  ticketId?: number
  companyId?: number
  date: number
  duration?: number
  text?: string
  typeId?: number
  booked?: boolean
  clearanceStatus?: 'NONE' | 'CLEARED'
}

export interface UninvoicedCompanyNode {
  name?: string
  sapCardCode?: string
  tickets: Record<string, TanssUninvoicedSupport[]>
}

export interface UninvoicedTenantNode {
  name: string
  code: string
  sortOrder: number
  companies: Record<string, UninvoicedCompanyNode>
}

export type UninvoicedTree = Record<string, UninvoicedTenantNode>

export interface TenantRef { id: string; name: string; code: string }

export function useUninvoicedTree(daysBack?: number) {
  return useQuery<{ data: UninvoicedTree; total: number; tenants: TenantRef[] }>({
    queryKey: ['tanss-sap', 'uninvoiced-tree', daysBack],
    queryFn: () => {
      const q = daysBack !== undefined ? `?daysBack=${daysBack}` : ''
      return apiClient.get(`/tanss-sap/uninvoiced-tree${q}`).then((r) => r.data)
    },
    staleTime: 60_000,
    enabled: daysBack === undefined || daysBack > 0,
  })
}

// ─── Texte generieren ─────────────────────────────────────────────────────────

export interface GenerateTextInput {
  id: number
  tenantId: string
  supports: TanssUninvoicedSupport[]
}

export interface GeneratedText {
  text: string
  generated: boolean
}

export function useGenerateTexts() {
  return useMutation<
    { data: Record<number, GeneratedText> },
    Error,
    GenerateTextInput[]
  >({
    mutationFn: (tickets) =>
      apiClient.post('/tanss-sap/generate-texts', { tickets }).then((r) => r.data),
  })
}

// ─── SAP Draft Preview ────────────────────────────────────────────────────────

export interface DraftPreviewInput {
  tenantId: string
  sapCardCode: string
  serviceItemCode?: string
  tickets: Array<{ id: number; totalMinutes: number; text: string }>
}

export function useDraftPreview() {
  return useMutation<{ data: Record<string, unknown> }, Error, DraftPreviewInput>({
    mutationFn: (input) =>
      apiClient.post('/tanss-sap/draft-preview', input).then((r) => r.data),
  })
}

// ─── Test-Setup ───────────────────────────────────────────────────────────────

export interface TestSetupResult {
  company: { id: number; name: string }
  ticket: { id: string; title: string }
  supports: Array<{ id: number; duration?: number; text?: string }>
}

export function useTestSetup() {
  return useMutation<{ data: TestSetupResult }, Error, void>({
    mutationFn: () => apiClient.post('/tanss-sap/test-setup', {}).then((r) => r.data),
  })
}

// ─── Dev-Mode Toggle ──────────────────────────────────────────────────────────

export function useDevMode() {
  return useQuery<{ data: { enabled: boolean } }>({
    queryKey: ['tanss-sap', 'dev-mode'],
    queryFn: () => apiClient.get('/tanss-sap/dev-mode').then((r) => r.data),
    staleTime: 5_000,
  })
}

export function useSetDevMode() {
  const qc = useQueryClient()
  return useMutation<{ data: { enabled: boolean } }, Error, boolean>({
    mutationFn: (enabled) => apiClient.put('/tanss-sap/dev-mode', { enabled }).then((r) => r.data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tanss-sap', 'dev-mode'] }) },
  })
}

// ─── SAP Submit (Weg B) ───────────────────────────────────────────────────────

export interface SubmitToSapInput {
  tenantId: string
  sapCardCode: string
  serviceItemCode?: string
  tickets: Array<{ id: number; supportIds: number[]; totalMinutes: number; text: string }>
}

export interface SubmitToSapResult {
  sapDocEntry: number
  sapDocNum: string
  sapPayload: Record<string, unknown>
  clearedSupports: number[]
  failedSupports: number[]
}

export function useSubmitToSap() {
  const qc = useQueryClient()
  return useMutation<{ data: SubmitToSapResult }, Error, SubmitToSapInput>({
    mutationFn: (input) => apiClient.post('/tanss-sap/submit-to-sap', input).then((r) => r.data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tanss-sap', 'uninvoiced-tree'] }) },
  })
}

// ─── Dev Dry-Run ─────────────────────────────────────────────────────────────

export function useDryRun() {
  const qc = useQueryClient()
  return useMutation<{ data: TanssSapDryRunResult }, Error, void>({
    mutationFn: () => apiClient.post('/tanss-sap/dry-run', {}).then((r) => r.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tanss-sap', 'sync-runs'] })
    },
  })
}

// ─── Test-Clear (Dev only) ────────────────────────────────────────────────────

export function useTestClearSupports() {
  const qc = useQueryClient()
  return useMutation<{ data: { cleared: number[]; failed: number[]; total: number } }, Error, number[]>({
    mutationFn: (supportIds) =>
      apiClient.post('/tanss-sap/test-clear-supports', { supportIds }).then((r) => r.data),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tanss-sap', 'uninvoiced-tree'] }) },
  })
}
